import { useCallback, useEffect, useState } from "react";
import { ArrowUpRight, RefreshCw, Newspaper } from "lucide-react";
import { api } from "@/lib/api";

const NEWS_REQUEST_TIMEOUT_MS = 120_000;

const CATEGORIES = ["All", "Medicare", "Medicaid", "Home Health", "NEMT", "Workforce", "Policy", "Industry"];

const CATEGORY_STYLES = {
  Medicare: "bg-blue-100 text-blue-900",
  Medicaid: "bg-purple-100 text-purple-900",
  "Home Health": "bg-emerald-100 text-emerald-900",
  NEMT: "bg-amber-100 text-amber-900",
  Workforce: "bg-rose-100 text-rose-900",
  Policy: "bg-slate-200 text-slate-900",
  Industry: "bg-cyan-100 text-cyan-900",
};

function formatDate(s) {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d.getTime())) return s.slice(0, 16);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

const MS_PER_MIN = 60_000;
const MS_PER_HOUR = 60 * MS_PER_MIN;
const MS_PER_DAY = 24 * MS_PER_HOUR;

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const diff = Date.now() - d.getTime();
  if (diff < MS_PER_HOUR) return `${Math.floor(diff / MS_PER_MIN)}m ago`;
  if (diff < MS_PER_DAY) return `${Math.floor(diff / MS_PER_HOUR)}h ago`;
  return `${Math.floor(diff / MS_PER_DAY)}d ago`;
}

function CategoryBadge({ category, size = "md" }) {
  const cls = CATEGORY_STYLES[category] || "bg-slate-100 text-slate-800";
  const sizeCls = size === "sm" ? "px-2 py-0.5 text-[10px] tracking-[0.12em]" : "px-3 py-1 text-[10px] tracking-[0.18em]";
  return (
    <span className={`${sizeCls} rounded-full font-semibold uppercase ${cls}`}>{category}</span>
  );
}

function DailyBriefCard({ data }) {
  if (!data?.daily_brief) return null;
  const date = new Date(data.generated_at);
  return (
    <div
      data-testid="news-daily-brief"
      className="mb-12 relative overflow-hidden rounded-2xl bg-gradient-to-br from-teh-deep via-teh-navy to-teh-navyDark text-white p-8 md:p-10"
    >
      <div
        className="absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-25"
        style={{ background: "radial-gradient(circle, #0E7A3D 0%, transparent 70%)" }}
      />
      <div className="relative flex flex-col md:flex-row md:items-start gap-6 md:gap-10">
        <div className="md:w-56 shrink-0">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-300">Today&apos;s brief</p>
          <p className="mt-2 font-serif text-3xl md:text-4xl leading-none">
            {date.toLocaleDateString("en-US", { month: "long", day: "numeric" })}
          </p>
          <p className="mt-1 text-xs text-white/60">
            {date.toLocaleDateString("en-US", { year: "numeric", weekday: "long" })}
          </p>
          <div className="mt-4 h-px w-12 bg-emerald-300/70" />
          <p className="mt-3 text-xs text-white/55">AI-synthesized from {data.item_count || 0} headlines</p>
        </div>
        <p className="flex-1 font-serif text-xl md:text-2xl leading-relaxed text-white/95 italic">
          &ldquo;{data.daily_brief}&rdquo;
        </p>
      </div>
    </div>
  );
}

function FeaturedCard({ item }) {
  return (
    <a
      href={item.source_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid="news-featured"
      className="lg:col-span-7 group relative flex flex-col p-8 md:p-10 bg-teh-deep text-white rounded-2xl overflow-hidden hover:shadow-2xl transition-all duration-500 min-h-[28rem]"
    >
      <div
        className="absolute -top-32 -right-32 w-96 h-96 rounded-full opacity-30"
        style={{ background: "radial-gradient(circle, #0E7A3D 0%, transparent 70%)" }}
      />
      <div className="relative flex items-center gap-3 mb-8">
        <CategoryBadge category={item.category} />
        <span className="text-xs text-white/60">{item.source_name}</span>
      </div>
      <h3 className="relative font-serif text-3xl md:text-4xl leading-[1.1] text-white">{item.title}</h3>
      <p className="relative mt-5 text-white/85 text-base leading-relaxed flex-grow">{item.summary}</p>
      <div className="relative mt-8 flex items-center justify-between pt-6 border-t border-white/15">
        <span className="text-xs text-white/60">{formatDate(item.published_date)}</span>
        <span className="inline-flex items-center gap-2 text-sm font-medium text-emerald-300 group-hover:gap-3 transition-all">
          Read full article
          <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
        </span>
      </div>
    </a>
  );
}

function SideListItem({ item, testId }) {
  return (
    <a
      href={item.source_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="group p-5 bg-white border border-teh-navy/10 rounded-xl hover:border-teh-navy/30 hover:shadow-lg transition-all duration-300"
    >
      <div className="flex items-center gap-2 mb-3">
        <CategoryBadge category={item.category} size="sm" />
        <span className="text-xs text-teh-muted truncate">{item.source_name}</span>
      </div>
      <h4 className="font-serif text-lg text-teh-deep leading-snug group-hover:text-teh-navy">{item.title}</h4>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-teh-muted">{formatDate(item.published_date)}</span>
        <ArrowUpRight className="w-4 h-4 text-teh-muted group-hover:text-teh-green group-hover:rotate-45 transition-all" />
      </div>
    </a>
  );
}

function GridCard({ item, testId }) {
  return (
    <a
      href={item.source_url}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className="group p-6 bg-white border border-teh-navy/10 rounded-xl hover:border-teh-navy/30 hover:shadow-lg transition-all duration-300"
    >
      <div className="flex items-center gap-2 mb-3">
        <CategoryBadge category={item.category} size="sm" />
      </div>
      <h4 className="font-serif text-lg text-teh-deep leading-snug group-hover:text-teh-navy mb-3">{item.title}</h4>
      <p className="text-sm text-teh-muted leading-relaxed line-clamp-3">{item.summary}</p>
      <div className="mt-4 flex items-center justify-between text-xs text-teh-muted">
        <span>{item.source_name}</span>
        <span>{formatDate(item.published_date)}</span>
      </div>
    </a>
  );
}

function FeedHeader({ data, loading, onRefresh }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
      <div className="max-w-2xl">
        <p className="teh-overline flex items-center gap-2">
          <Newspaper className="w-4 h-4" strokeWidth={1.8} />
          Healthcare Dashboard
        </p>
        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mt-4 text-teh-deep leading-[1.05] tracking-tight">
          The pulse of healthcare,
          <br />
          <span className="italic text-teh-green">curated for you.</span>
        </h2>
        <p className="mt-5 text-teh-muted text-base md:text-lg max-w-xl">
          Auto-generated by AI from CMS, KFF, Medicare/Medicaid, and home-health policy sources.
          Refreshed every 12 hours.
        </p>
      </div>
      <div className="flex items-center gap-3">
        {data?.generated_at && (
          <span className="text-xs text-teh-muted" data-testid="news-updated">
            Updated {timeAgo(data.generated_at)}
          </span>
        )}
        <button
          onClick={onRefresh}
          disabled={loading}
          data-testid="news-refresh"
          className="inline-flex items-center gap-2 px-4 py-2 border border-teh-navy/15 rounded-full text-xs font-medium text-teh-navy hover:bg-white transition disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </div>
    </div>
  );
}

function FilterPills({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2 mb-10" data-testid="news-filters">
      {CATEGORIES.map((c) => (
        <button
          key={c}
          onClick={() => onChange(c)}
          data-testid={`news-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
          className={`px-4 py-1.5 rounded-full text-xs font-medium tracking-wide transition ${
            value === c
              ? "bg-teh-navy text-white"
              : "bg-white text-teh-muted border border-teh-navy/15 hover:border-teh-navy hover:text-teh-navy"
          }`}
        >
          {c}
        </button>
      ))}
    </div>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      <div className="lg:col-span-7 h-80 bg-white border border-teh-navy/10 rounded-2xl animate-pulse" />
      <div className="lg:col-span-5 space-y-4">
        {[0, 1, 2].map((i) => (
          <div key={i} className="h-24 bg-white border border-teh-navy/10 rounded-xl animate-pulse" />
        ))}
      </div>
    </div>
  );
}

export default function HealthcareNewsFeed({ compact = false, limit = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("All");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get("/news/healthcare", { timeout: NEWS_REQUEST_TIMEOUT_MS });
      setData(res.data);
    } catch (e) {
      setError("Couldn't load the feed right now. Please try again shortly.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const items = data?.items || [];
  const filteredAll = filter === "All" ? items : items.filter((i) => i.category === filter);
  const filtered = limit ? filteredAll.slice(0, limit) : filteredAll;
  const featured = filtered[0];
  const sideItems = filtered.slice(1, 5);
  const remaining = filtered.slice(5);

  return (
    <section data-testid="news-feed" className={`${compact ? "py-16" : "py-24 md:py-28"} bg-teh-bg`}>
      <div className="teh-container">
        <FeedHeader data={data} loading={loading} onRefresh={load} />
        <DailyBriefCard data={data} />
        <FilterPills value={filter} onChange={setFilter} />

        {loading && !data && <LoadingSkeleton />}

        {error && (
          <div className="p-8 bg-rose-50 border border-rose-200 rounded-2xl text-rose-900" data-testid="news-error">
            {error}
          </div>
        )}

        {!loading && !error && filtered.length === 0 && (
          <div className="p-8 bg-white border border-teh-navy/10 rounded-2xl text-teh-muted">
            No items in the <strong>{filter}</strong> category right now. Try another filter or refresh.
          </div>
        )}

        {featured && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-testid="news-grid">
            <FeaturedCard item={featured} />
            <div className="lg:col-span-5 flex flex-col gap-4">
              {sideItems.map((item) => (
                <SideListItem key={item.source_url} item={item} testId={`news-item-${item.source_url}`} />
              ))}
            </div>
          </div>
        )}

        {!compact && remaining.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {remaining.map((item) => (
              <GridCard key={item.source_url} item={item} testId={`news-item-extra-${item.source_url}`} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
